import { usePathSegments } from '@/hooks/usePathSegments';
import { locales } from '@/utils/testConfig';
import { invariant } from '@asurion-core/invariant';
import {
  BasePath,
  getProductConfigForD2CAppRoute,
  getProductConfigForPresentationRoutes,
  PartnerIdentifier,
  PresentationIdentifier,
  ProductConfigWithSku,
  ProductIdentifier,
} from '@sales-domain/product-config';
import { useParams, usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

export type ParamsType = {
  lang?: locales;
  partner: PartnerIdentifier;
  product: ProductIdentifier;
  presentation: PresentationIdentifier;
};

export function useProductConfig(): ProductConfigWithSku;
export function useProductConfig({
  allowUndefinedProductConfig,
}: {
  allowUndefinedProductConfig: true;
}): ProductConfigWithSku | undefined;
export function useProductConfig({
  allowUndefinedProductConfig,
}: { allowUndefinedProductConfig?: true } = {}) {
  const { basePath, partnerIdentifier, productIdentifier } = usePathSegments();
  const params = useParams<ParamsType>();
  const pathName = usePathname();

  // state
  const [isClient, setIsClient] = useState(false);
  useEffect(() => setIsClient(true), []);

  try {
    // Use case #1: Using product, partner and presentation
    if (params?.product && params?.partner && params?.presentation) {
      return getProductConfigForPresentationRoutes(
        params.product as ProductIdentifier,
        params.partner as PartnerIdentifier,
        params.presentation as PresentationIdentifier,
      );
    }
    if (pathName.includes(PresentationIdentifier.PrePaid)) {
      return getProductConfigForPresentationRoutes(
        params.product as ProductIdentifier,
        params.partner as PartnerIdentifier,
        PresentationIdentifier.PrePaid,
      );
    }

    invariant(basePath != null, 'Base path is not defined');

    // Use case #2: NOT Using presentation, but using partner/product
    if (basePath === BasePath.Affiliate) {
      return getProductConfigForD2CAppRoute(
        basePath as BasePath,
        partnerIdentifier as PartnerIdentifier,
        productIdentifier as ProductIdentifier,
      );
    }

    // Use case #3: NOT Using presentation, but using ONLY partner
    return getProductConfigForD2CAppRoute(
      basePath as BasePath,
      partnerIdentifier as PartnerIdentifier,
    );
  } catch (error) {
    if (!allowUndefinedProductConfig) {
      console.error(`Error getting product config`, {
        error,
        pathname: isClient ? window?.location.pathname : '',
      });

      throw error;
    }
  }
}
